import type { AuthChangeEvent, Session } from '@supabase/supabase-js'
import { useEffect, useRef } from 'react'

import { onAuthStateChange } from '@/modules/auth/service'

type AuthEventHandler = (session: Session | null) => void

interface AuthStateHandlers {
  onInitialSession?: AuthEventHandler
  onSignedIn?: AuthEventHandler
  onSignedOut?: AuthEventHandler
  onTokenRefreshed?: AuthEventHandler
  onPasswordRecovery?: AuthEventHandler
  onUserUpdated?: AuthEventHandler
}

// A Map, so an event named after an Object prototype key cannot reach one.
const HANDLER_BY_EVENT = new Map<AuthChangeEvent, keyof AuthStateHandlers>([
  ['INITIAL_SESSION', 'onInitialSession'],
  ['SIGNED_IN', 'onSignedIn'],
  ['SIGNED_OUT', 'onSignedOut'],
  ['TOKEN_REFRESHED', 'onTokenRefreshed'],
  ['PASSWORD_RECOVERY', 'onPasswordRecovery'],
  ['USER_UPDATED', 'onUserUpdated'],
])

// Subscribes once: Supabase replays INITIAL_SESSION to every new subscriber,
// so resubscribing per render re-fires every connect handler.
export const useAuthStateChange = (handlers: AuthStateHandlers) => {
  const latestHandlers = useRef(handlers)

  useEffect(() => {
    latestHandlers.current = handlers
  })

  useEffect(() => {
    // Supabase holds its auth lock during this callback; calling it back from a handler deadlocks.
    const { data } = onAuthStateChange((event, session) => {
      const handlerName = HANDLER_BY_EVENT.get(event)
      if (!handlerName) return
      setTimeout(() => latestHandlers.current[handlerName]?.(session), 0)
    })

    return () => data.subscription.unsubscribe()
  }, [])
}
